/**
 * Quando os botões de baixar o laudo eletrônico esperam, e por quê (F-E162.F2-02, QA-21).
 *
 * A tela já mostrava que o laudo não estava pronto — a faixa do próximo passo e a pré-visualização
 * sem documento — e os dois botões seguiam ativos, levando a uma recusa que a tela já sabia. A
 * regra segue a mesma leitura da prontidão que a faixa mostra (`prontidao.laudo`, a régua do
 * portão do servidor) e o que a pré-visualização recebeu:
 *
 * - o servidor segura o laudo → param e dizem o motivo (com as peças, quando ele as nomeia);
 * - a pré-visualização foi recusada como não pronta → param;
 * - a prontidão ainda está sendo lida → param, com o aviso de que se está conferindo;
 * - a prontidão não pôde ser lida (falha ou retrato não lido) → seguem ativos: o servidor confere
 *   de novo no download e a recusa dele aparece com o motivo específico.
 */
import type { ViewerStatus } from '@/components/LaudoPdfPreview';
import { ptBR } from '@/i18n/pt-BR';
import type { LiberacaoDaSaidaData } from '@/lib/resultadoAuditado';
import type { EstadoDoResultadoNaTela } from '@/lib/useResultadoDoCaso';

const t = ptBR.laudoEletronico;
const tp = t.prontidaoDoDownload;

export interface BloqueioDoDownload {
  estado: 'carregando' | 'bloqueado';
  texto: string;
}

function textoDoMotivo({ motivo, pendentes }: LiberacaoDaSaidaData): string {
  if (motivo === null) return t.naoPronto;
  const porque = ptBR.resultadoAuditado.motivoDaProntidao[motivo];
  if (pendentes.length === 0) return tp.bloqueado.replace('{motivo}', porque);
  return tp.bloqueadoComPecas.replace('{motivo}', porque).replace('{pecas}', pendentes.join(', '));
}

/** `null` = os botões seguem ativos; senão, o estado e o texto que explica a espera. */
export function bloqueioDoDownload(
  resultado: EstadoDoResultadoNaTela,
  preview: ViewerStatus,
): BloqueioDoDownload | null {
  const laudo = resultado.estado === 'pronto' ? resultado.dados.prontidao?.laudo : undefined;
  if (laudo !== undefined && !laudo.liberada) {
    return { estado: 'bloqueado', texto: textoDoMotivo(laudo) };
  }
  if (preview === 'empty') return { estado: 'bloqueado', texto: tp.semDocumento };
  if (resultado.estado === 'carregando') return { estado: 'carregando', texto: tp.carregando };
  return null;
}
